import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login } from "../../../service/auth/authService";
import logo from "../../../assets/logo/uxa_long_logo_white.png";
import background from "../../../assets/background/background_1.jpg";
import { User } from "../../../types/userTypes";

const Login: React.FC = () => {
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const handleLogin = async () => {
        if (username.trim() === "" || password.trim() === "") {
            toast.error("Please enter both username and password", { position: "top-right" });
            return;
        }
        setLoading(true);
        try {
            const response = await login(username, password);
            const { token, user }: { token: string; user: User } = response.data;
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(user));
            toast.success("Login successful", { position: "top-right" });
            navigate("/");
            window.location.reload();
        } catch (error: any) {
            toast.error(error.message, { position: "top-right" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen">
            {/* Left Side Banner with Background Image and Grey Overlay */}
            <div
                className="w-full md:w-3/5 flex flex-col items-center justify-center text-white p-10 md:p-16 relative bg-cover bg-center"
                style={{ backgroundImage: `url(${background})` }}
            >
                {/* Opacity Grey Overlay */}
                <div className="absolute inset-0 bg-black opacity-50"></div>

                <div className="relative z-10 text-center">
                    <h1 className="text-3xl md:text-5xl font-bold flex items-center space-x-2">
                        <span>Virtual Data Room</span>
                    </h1>
                </div>
                <div className="relative z-10 text-left mt-1">
                    <span className="text-sm md:text-lg font-semibold mt-1 flex items-center space-x-2">
                        <span>by</span>
                        <img src={logo} alt="Logo" className="w-48 md:w-52 h-auto" />
                    </span>
                </div>

                {/* Copyright Notice */}
                <p className="absolute bottom-4 text-sm text-gray-300 text-center">
                    &copy; {new Date().getFullYear()} Virtual Data Room | Uxatelier. All rights reserved.
                </p>
            </div>

            {/* Right Side Login Form */}
            <div className="w-full md:w-2/5 flex flex-col justify-center p-10 md:p-20">
                <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8">Login</h2>

                {/* Login Form */}
                <div className="w-full max-w-md">
                    <label className="block text-lg md:text-xl font-medium text-gray-700">Username</label>
                    <input
                        type="text"
                        className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]"
                        placeholder="Enter Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />

                    <label className="block text-lg md:text-xl font-medium text-gray-700 mt-4">Password</label>
                    <input
                        type="password"
                        className="w-full mt-2 p-3 border rounded-lg text-lg focus:ring-2 focus:ring-[#632DE9]"
                        placeholder="Enter Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <button
                        className="w-full mt-6 bg-[#632DE9] hover:bg-[#4A1FB8] text-white font-bold p-2 rounded-lg text-lg transition duration-300"
                        onClick={handleLogin}
                        disabled={loading}
                    >
                        {loading ? "Logging in..." : "Login"}
                    </button>

                    {/* Register Link */}
                    <p className="text-center text-sm text-gray-600 mt-3">
                        Don't have an account?{" "}
                        <a href="/register" className="text-blue-500 hover:underline">
                            Register here
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
